<script setup lang="ts">
import { computed } from 'vue'

import { opChain } from '@collector/shared-utils'
import type { SubParticipant } from '@collector/sportsapi-types'

import type { LineupConfig } from '../../../../interfaces/LineupConfig'
import type { LineupModel } from '../../../../interfaces/LineupModel'
import { cloneWithKey } from '../../../../utils/model.utils'
import { isPlayerSame } from '../../../../utils/player.utils'
import SectionEmpty from '../SectionEmpty.vue'
import SectionTitle from '../SectionTitle.vue'
import SeasonSquadSectionItem from './SeasonSquadSectionItem.vue'
import { canAddParticipantSubTypeToPosition } from './utils'

type Props = {
  config: LineupConfig
  squad?: SubParticipant[]
}
const props = withDefaults(defineProps<Props>(), {
  squad: () => [],
})

// state
const lineup = defineModel<LineupModel>({ required: true })

// computed
const lineupFlat = computed(() => Object.values(lineup.value).flat())
const availableSquad = computed(() =>
  props.squad.filter(
    (participant) =>
      !lineupFlat.value.find((lineupParticipant) =>
        isPlayerSame(lineupParticipant, participant),
      ),
  ),
)

// methods
function addPlayer(key: PropertyKey, player: SubParticipant): void {
  const updatedLineup = { ...lineup.value }
  updatedLineup[key] = [...(updatedLineup[key] || []), player]
  lineup.value = updatedLineup
}

function addAll(): void {
  const addAllTargets = props.config.addPlayer.addAllTargets
  if (addAllTargets && addAllTargets?.length > 0) {
    const updatedLineup = cloneWithKey(lineup, ...addAllTargets)
    const squadToAdd = new Set(availableSquad.value)
    for (const targetPositionKey of addAllTargets)
      addToPosition(targetPositionKey, squadToAdd, updatedLineup)

    lineup.value = updatedLineup
  }
}

function addToPosition(
  positionKey: PropertyKey,
  squadToAdd: Set<SubParticipant>,
  updatedLineup: LineupModel,
): void {
  const positionLineup = updatedLineup[positionKey]
  const positionConfig = props.config.positions[positionKey]
  const addSquadConfig = props.config.positions[positionKey].addSquadPlayer

  const squadToAddFiltered = [...squadToAdd].filter((p) => {
    const forcedSubType = addSquadConfig?.addAllForceSubType
    const validSubType = !forcedSubType || p.details?.subtype === forcedSubType
    return validSubType
  })

  for (const player of squadToAddFiltered) {
    const availableSize =
      positionLineup.length < (positionConfig.maxCount ?? Infinity)
    const subType = player.details?.subtype ?? null
    const allowedSubType =
      !addSquadConfig?.disableForSubTypes?.includes(subType) &&
      canAddParticipantSubTypeToPosition(player.details?.subtype, positionKey)

    if (availableSize) {
      if (allowedSubType) {
        positionLineup.push(player)
        squadToAdd.delete(player)
      }
    } else break
  }
}
</script>

<template>
  <div class="grid grid-cols-1 gap-1">
    <SectionTitle
      label="Season squad"
      :count="availableSquad.length"
      :showButton="!!opChain(config.addPlayer.addAllTargets, (v) => v.length)"
      buttonText="ADD ALL"
      :buttonConfirmText="config.addPlayer.addAllConfirmText"
      @actionConfirmed="addAll"
    />
    <SectionEmpty v-if="availableSquad.length === 0">
      No season squad players available
    </SectionEmpty>
    <SeasonSquadSectionItem
      v-for="player in availableSquad"
      :key="player.id"
      :config
      :lineup
      :player
      @add="(key, player) => addPlayer(key, player)"
    />
  </div>
</template>

<style scoped></style>
